/**
 * Search helpers for the orcaloops.ai/connect approve page (OL-362).
 *
 * TanStack Router JSON-parses each search value, so `state=12345` arrives as
 * the number `12345` and `flag=true` as a boolean. The page never interprets
 * the OAuth params — it hands the raw query back to `oauthConsent.*`, which
 * re-validates it — so these helpers only put the values back into strings.
 *
 * Plain functions with no atom/RPC imports: the eager route half uses them, so
 * they land in the always-loaded entry chunk and must stay cheap.
 *
 * @since 1.0.0
 */

/**
 * Keep the string-valued search params, turn numbers and booleans back into
 * `String(value)`, and drop everything else (objects, arrays, `null`).
 *
 * @since 1.0.0
 */
export const consentSearchFrom = (search: Record<string, unknown>): Readonly<Record<string, string>> =>
  Object.fromEntries(
    Object.entries(search).flatMap(([key, value]) =>
      typeof value === "string" || typeof value === "number" || typeof value === "boolean"
        ? [[key, String(value)]]
        : []
    ),
  );

/**
 * The raw `/connect` query (no leading `?`) the server re-validates.
 *
 * @since 1.0.0
 */
export const consentQueryFrom = (search: Readonly<Record<string, string>>): string =>
  new URLSearchParams(search).toString();
